import { Sequence } from '@/types/sequence';
import { DEFAULTS } from '@/constants/app.constants';

/**
 * TypeScript interfaces for Typesense hit data structure
 * These provide type safety for the data coming from Typesense
 */
export interface TypesenseHitDocument {
  id: string;
  title?: string;
  name?: string;
  price?: string | number;
  is_free?: boolean;
  software?: string;
  software_type?: string;
  holiday?: string;
  image_url?: string;
  thumbnail_url?: string;
  preview_url?: string;
  album_cover_url?: string;
  song_artist?: string;
  artist_name?: string;
  album?: string;
  spotify_duration_ms?: number;
  duration_seconds?: number;
  genre?: string;
  genre_name?: string;
  created_at?: string;
  updated_at?: string;
  download_count?: number;
  is_featured?: boolean;
  featured?: boolean;
  user_id?: string;
}

export interface TypesenseHit {
  document: TypesenseHitDocument;
  highlights?: any[];
  text_match?: number;
}

/**
 * Service for transforming Typesense search results to Sequence objects
 * Consolidates duplicate transformation logic across the application
 */
export class SequenceTransformService {
  /**
   * Transform a single Typesense hit to a Sequence object
   */
  static transformHitToSequence(hit: TypesenseHit): Sequence {
    const item = hit.document;
    
    return {
      id: item.id,
      title: item.title || item.name || '',
      displayName: item.name || item.title || '',
      price: this.parsePrice(item.price),
      isFree: item.is_free || false,
      software: item.software || item.software_type || DEFAULTS.SOFTWARE,
      software_type: item.software_type || item.software || DEFAULTS.SOFTWARE,
      holiday: item.holiday || DEFAULTS.HOLIDAY,
      imageUrl: this.selectBestImageUrl(item),
      song: {
        id: item.id,
        title: item.title || item.name || '',
        artist: this.extractArtistName(item),
        album: item.album || '',
        duration: this.parseDuration(item),
        genre: item.genre || item.genre_name || DEFAULTS.GENRE,
        imageUrl: this.selectBestImageUrl(item),
      },
      createdAt: item.created_at || '',
      downloadCount: item.download_count || 0,
      downloads: item.download_count || 0,
      averageRating: 0, // Not available in current schema
      rating: 0,
      ratingCount: 0,
      featured: item.is_featured || item.featured || false,
      userId: item.user_id || '',
      creatorId: item.user_id || '',
      creatorName: DEFAULTS.CREATOR, // Not available in current schema
      creatorAvatar: '',
      created_at: item.created_at || '',
      updated_at: item.updated_at || '',
    };
  }

  /**
   * Transform multiple Typesense hits to Sequence objects
   */
  static transformHitsToSequences(hits: TypesenseHit[]): Sequence[] {
    return hits.map(hit => this.transformHitToSequence(hit));
  }

  /**
   * Extract artist name with proper fallback logic
   * Addresses the "Unknown Artist" bug by checking multiple fields
   */
  private static extractArtistName(item: TypesenseHitDocument): string {
    // Check song_artist first (most specific), then artist_name, then fallback
    return item.song_artist || item.artist_name || DEFAULTS.ARTIST;
  }

  /**
   * Parse price from string or number to number
   */
  private static parsePrice(price: string | number | undefined): number {
    if (typeof price === 'number') return price;
    if (typeof price === 'string') return parseFloat(price) || 0;
    return 0;
  }

  /**
   * Parse duration from various formats to number (seconds)
   */
  private static parseDuration(item: TypesenseHitDocument): number {
    // Prefer duration_seconds, then calculate from spotify_duration_ms
    if (item.duration_seconds) return item.duration_seconds;
    if (item.spotify_duration_ms) return Math.floor(item.spotify_duration_ms / 1000);
    return 0;
  }

  /**
   * Select the best available image URL from multiple possible fields
   */
  private static selectBestImageUrl(item: TypesenseHitDocument): string {
    return item.image_url || 
           item.thumbnail_url || 
           item.preview_url || 
           item.album_cover_url || 
           '';
  }

  /**
   * Validate that a Typesense hit has required fields
   * Useful for debugging and ensuring data quality
   */
  static validateHit(hit: TypesenseHit): boolean {
    const item = hit.document;
    return !!(item.id && (item.title || item.name));
  }

  /**
   * Get transformation statistics for debugging
   * Returns info about field usage across a set of hits
   */
  static getTransformationStats(hits: TypesenseHit[]): {
    totalHits: number;
    hasTitle: number;
    hasName: number;
    hasArtistName: number;
    hasSongArtist: number;
    hasImageUrl: number;
    hasPrice: number;
    isFree: number;
  } {
    return hits.reduce((stats, hit) => {
      const item = hit.document;
      return {
        totalHits: stats.totalHits + 1,
        hasTitle: stats.hasTitle + (item.title ? 1 : 0),
        hasName: stats.hasName + (item.name ? 1 : 0),
        hasArtistName: stats.hasArtistName + (item.artist_name ? 1 : 0),
        hasSongArtist: stats.hasSongArtist + (item.song_artist ? 1 : 0),
        hasImageUrl: stats.hasImageUrl + (item.image_url ? 1 : 0),
        hasPrice: stats.hasPrice + (item.price ? 1 : 0),
        isFree: stats.isFree + (item.is_free ? 1 : 0),
      };
    }, {
      totalHits: 0,
      hasTitle: 0,
      hasName: 0,
      hasArtistName: 0,
      hasSongArtist: 0,
      hasImageUrl: 0,
      hasPrice: 0,
      isFree: 0,
    });
  }
}